import { Component } from '@theme/component';

/**
 * @typedef {Object} TextCollapseRefs
 * @property {HTMLButtonElement} toggle - Read more / Read less button
 */

/**
 * @extends {Component<TextCollapseRefs>}
 */
class TextCollapse extends Component {
  #originalHTML = null;
  #toggleHTML = null;
  #resizeObserver = null;
  #renderScheduled = false;

  connectedCallback() {
    super.connectedCallback();

    const target = this.#getTarget();
    const toggle = this.refs.toggle;
    if (!target || !toggle) return;

    toggle.removeAttribute('hidden');
    this.#toggleHTML = toggle.outerHTML;
    toggle.remove();
    this.#originalHTML = target.innerHTML;

    this.#render();

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleRender());
    this.#resizeObserver.observe(target);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  toggle() {
    this.dataset.expanded = this.dataset.expanded === 'true' ? 'false' : 'true';
    this.#render();
    this.querySelector('.text-collapse__toggle')?.setAttribute('aria-expanded', this.dataset.expanded);
  }

  #scheduleRender() {
    if (this.#renderScheduled) return;
    this.#renderScheduled = true;
    requestAnimationFrame(() => {
      this.#renderScheduled = false;
      this.#render();
    });
  }

  #getTarget() {
    return this.querySelector(':scope > rte-formatter, :scope > div');
  }

  #render() {
    const target = this.#getTarget();
    if (!target || !this.#originalHTML || !this.#toggleHTML) return;

    target.style.maxHeight = '';
    target.style.overflow = '';
    target.innerHTML = this.#originalHTML;

    if (this.dataset.expanded === 'true') {
      this.#appendToggle(target);
    } else {
      this.#truncate(target);
    }
  }

  #appendToggle(target) {
    const lastBlock = this.#findLastNonEmptyBlock(target) || target;
    while (lastBlock.lastChild) {
      const node = lastBlock.lastChild;
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
        node.remove();
        continue;
      }
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
        node.remove();
        continue;
      }
      break;
    }
    lastBlock.insertAdjacentHTML('beforeend', ' ' + this.#toggleHTML);
  }

  #truncate(target) {
    const cropLines = parseInt(this.style.getPropertyValue('--crop-lines'), 10) || 3;
    const lineHeight = parseFloat(getComputedStyle(target).lineHeight);
    const maxHeight = lineHeight * cropLines + 1;

    this.#appendToggle(target);

    if (target.scrollHeight <= maxHeight) {
      return;
    }

    const fullText = this.#extractText(target);
    if (!fullText) return;

    target.style.maxHeight = `${maxHeight}px`;
    target.style.overflow = 'hidden';
    target.innerHTML = '';

    const paragraph = document.createElement('p');
    const textNode = document.createTextNode(fullText);
    paragraph.appendChild(textNode);
    paragraph.insertAdjacentHTML('beforeend', ' ' + this.#toggleHTML);
    target.appendChild(paragraph);

    const tokens = fullText.split(/(\s+)/);
    let safety = 1000;

    while (target.scrollHeight > maxHeight && tokens.length > 1 && safety-- > 0) {
      tokens.pop();
      textNode.textContent = tokens.join('').replace(/\s+$/, '');
    }

    textNode.textContent = textNode.textContent.replace(/[\s.,;:]+$/, '') + '… ';
  }

  #extractText(root) {
    const clone = root.cloneNode(true);
    clone.querySelector('.text-collapse__toggle')?.remove();
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  #findLastNonEmptyBlock(root) {
    const blocks = root.querySelectorAll('p, div, li, blockquote, h1, h2, h3, h4, h5, h6');
    for (let i = blocks.length - 1; i >= 0; i--) {
      if (blocks[i].textContent.trim()) return blocks[i];
    }
    return null;
  }
}

if (!customElements.get('text-collapse')) {
  customElements.define('text-collapse', TextCollapse);
}
